import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import i18n from 'i18next';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { api, clearAuthTokens } from '@/services/api';
import { ACTIVE_ROLE_MISMATCH_EVENT } from '@/lib/auth-events';
import { useToast } from '@/components/ui/use-toast';
import { authStorage } from '@/services/storage';
import {
  announceAuthChange, IMPERSONATION_ENDED_EVENT, setTabOwner, startAuthSync, tabOwner,
} from '@/lib/auth-sync';
import { isNative } from '@/lib/platform';
import { setViewerTimeZone } from '@/lib/trfTime';
import { homeFor } from '@/lib/home-route';
import { submitRequiredPasswordChange, signInWithGoogle } from '@/services/authExtras';
import { clearUserScopedStorage } from '@/lib/user-scoped-storage';
import {
  accountKeyOf, clearReturnPath, consumeReturnPath, expiryLanguageFor, forgetSignedInTab, markSessionExpired,
  markSignedInTabEnded,
} from '@/lib/return-path';
import type { User, LoginResponse, LoginRequest, RegisterRequest, TranslationSettings, FeatureFlags } from '@/types/api';

/** Typed error that carries a machine-readable code for callers to branch on. */
export class AppError extends Error {
  attemptsRemaining?: number;

  constructor(
    message: string,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'AppError';
  }
}

/** An axios failure as an AppError carrying the server's string `code` (#2324). */
function toAppError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; code?: string } | undefined;
    return new AppError(data?.message || fallback, data?.code);
  }
  return error instanceof Error ? error : new Error(fallback);
}

export interface ImpersonationState {
  active: true;
  originalToken: string;
  originalUser: User;
  targetUser: User;
  allowedMenus: string[];
}

interface AuthContextType {
  user: User | null;
  /**
   * Org-wide translation settings, loaded from /auth/me alongside the
   * user payload. Null until /auth/me resolves (or on logout). Used by
   * handoff chat surfaces to decide disclosureMode without a separate
   * fetch.
   */
  translation: TranslationSettings | null;
  /**
   * Server-authoritative feature flags from /auth/me. Null until it resolves
   * (or on logout) — treat null/false as OFF. Used to hide entry points (e.g.
   * the F-1 bill/EOB surface); server routes remain the source of truth.
   */
  features: FeatureFlags | null;
  loading: boolean;
  /**
   * #2439 — the boot session check could not answer (a network error, a 5xx):
   * a session may be live even though `user` is null. A page that would
   * replace whoever is signed in (the magic link) must not read this as
   * "nobody".
   */
  sessionCheckFailed: boolean;
  /**
   * #2641 — ask /auth/me again now (the "Try again" button). While
   * `sessionCheckFailed` is set and nobody is adopted, the provider also
   * retries on its own: after 1 s, 2 s, 4 s, then every 10 s.
   */
  retrySessionCheck: () => void;
  login: (credentials: LoginRequest, rememberMe?: boolean) => Promise<void>;
  loginWithMagicLink: (token: string, rememberMe?: boolean) => Promise<void>;
  loginWithSmsCode: (email: string, code: string, rememberMe?: boolean) => Promise<void>;
  /** #2324 — finish a held sign-in: the change token plus a new password. */
  completeRequiredPasswordChange: (changeToken: string, newPassword: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: (credential: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterRequest) => Promise<{ pendingApproval: boolean }>;
  /**
   * L4-b / D5: `rememberMe` defaults to true — an applicant is kept signed in
   * on the device they sign up on (same 7-day policy as Login's checkbox), so
   * "Save anytime — pick up where you left off" holds after Safari closes.
   */
  applySignup: (data: { email: string; password: string; first_name: string }, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * #2439 — end the current session without leaving the page: the server
   * logout (revokes the session, clears the cookie) and the same local clears
   * as `logout`, but no navigate. For a page that signs someone else in next
   * (a magic link opened over a live session).
   */
  signOutQuietly: () => Promise<void>;
  switchRole: (role: User['role']) => Promise<void>;
  isAuthenticated: boolean;
  canSwitchRole: boolean;
  /** MFA verify's sign-in: adopt the user, then hydrate `permissions` from /me. */
  setUserFromTokens: (user: User) => Promise<void>;
  /**
   * Re-fetch /auth/me and refresh the cached user (and translation envelope).
   * Used after the re-consent flow clears a block so legal_consent.blocked
   * flips back to false without a full page reload.
   */
  refresh: () => Promise<void>;
  impersonation: ImpersonationState | null;
  startImpersonation: (token: string, targetUser: User, allowedMenus: string[]) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// The resync toast (B-596), one whole sentence per role in the reader's
// language (#2109 R3 L8) — the role keys are persisted identifiers, not copy,
// so a role word is never spliced into an English frame. A role with no
// sentence falls back to `roleResync.title.unknown` with the raw value.
const ROLE_RESYNC_TITLE_KEY = {
  parent: 'feedback:roleResync.title.parent',
  trfer: 'feedback:roleResync.title.trfer',
  admin: 'feedback:roleResync.title.admin',
  applicant: 'feedback:roleResync.title.applicant',
} as const satisfies Partial<Record<User['role'], string>>;

const IMPERSONATION_KEY = 'impersonation_session';

/**
 * Who the React Query cache belongs to (#2330). Query keys carry no user and
 * no role (`['grants-mine']` is one key for every parent), so the cache is
 * only correct for the identity that was signed in when it filled. The key is
 * the account plus its ACTIVE role, because role-scoped endpoints answer for
 * the active role (#2096).
 */
function identityOf(u: User | null): string | null {
  if (!u) return null;
  return `${u.id ?? u._id ?? u.email}:${u.role}`;
}

/** The account id the cross-tab guard compares (#2439): the access token's `id` claim. */
function sessionOwnerIdOf(u: User | null): string | null {
  if (!u) return null;
  const id = u.id ?? u._id;
  return id === undefined || id === null || id === '' ? null : String(id);
}

/**
 * The account a user payload names, whichever id field it carries (#2936):
 * the impersonate endpoint returns the raw doc (`_id`), /me returns `id`.
 */
function accountIdOf(u: User): string {
  return String(u.id ?? u._id ?? u.email);
}

/** The impersonation session this tab is running, or null. */
function liveImpersonation(): ImpersonationState | null {
  try {
    const saved = sessionStorage.getItem(IMPERSONATION_KEY);
    return saved ? (JSON.parse(saved) as ImpersonationState) : null;
  } catch {
    return null;
  }
}

/** The operator of the impersonation this tab is running, or null. */
function liveImpersonationOperator(): User | null {
  return liveImpersonation()?.originalUser ?? null;
}

/** Where an operator starts an impersonation again (#2936, #2478). */
function impersonationRestartHint(role: User['role']): string {
  return `Start it again from ${role === 'admin' ? 'Access control' : 'Users'}.`;
}

/** The screen an operator starts impersonations from: Access control or Users. */
function usersScreenFor(role: User['role']): string {
  return role === 'admin' ? '/admin/users' : '/trfer/users';
}

/** #2641: the boot session check's retry schedule while the server is unreachable. */
export const SESSION_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 10_000] as const;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [translation, setTranslation] = useState<TranslationSettings | null>(null);
  const [features, setFeatures] = useState<FeatureFlags | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionCheckFailed, setSessionCheckFailed] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // #2330: the identity whose data the query cache currently holds.
  // `undefined` = nothing adopted yet (cold boot), so the boot /me does not
  // clear the cache it is about to fill.
  const cacheIdentityRef = useRef<string | null | undefined>(undefined);

  /**
   * The ONLY way the provider sets `user`. Before a DIFFERENT identity renders
   * — another account, another active role, or signed-out — every cached query
   * is dropped, so no frame can pair the new identity with the old one's data.
   * Seen on staging 2026-10-08 (#2330): a parent who signed in by magic link
   * on an open page was shown the previous parent's grants. A re-read of the
   * SAME identity (`refresh()` after /me) keeps the cache, or every page would
   * refetch on every /me.
   *
   * `freshSession` is for the sign-in paths: a sign-in starts a new session
   * even for the same account, so it always clears. Callers write the new
   * token BEFORE calling this, so any refetch the clear triggers goes out as
   * the new identity — the same ordering as `switchRole` (#2096).
   *
   * The per-person browser storage (Williams/Stevens thread ids, voice
   * resume, dashboard layout cache — `lib/user-scoped-storage.ts`) goes at the
   * same moment, for the same reason (#2340).
   */
  const adoptUser = (
    next: User | null,
    { freshSession = false, announce = true }: { freshSession?: boolean; announce?: boolean } = {},
  ) => {
    // #2439: the cross-tab identity guard (lib/auth-sync.ts) tracks the
    // session OWNER: the account itself, or the operator during an
    // impersonation. Every /me during an impersonation answers for the
    // target, so the owner is read from the live impersonation session
    // (sessionStorage, written before the swap), never from `next`. A change
    // is announced to the other tabs of this browser, unless `announce` is
    // false: a local clear after a failed refresh is this tab's business, and
    // the shared cookie may still be good for the others.
    const ownerId = sessionOwnerIdOf(next === null ? null : liveImpersonationOperator() ?? next);
    if (ownerId !== tabOwner()) {
      setTabOwner(ownerId);
      if (announce) announceAuthChange(ownerId);
    }
    const nextIdentity = identityOf(next);
    const prev = cacheIdentityRef.current;
    if (freshSession || (prev !== undefined && prev !== nextIdentity)) {
      queryClient.clear();
      clearUserScopedStorage();
    }
    cacheIdentityRef.current = nextIdentity;
    // #2741: staff times follow the browser unless this person chose a zone.
    // An empty setting (new users carry none) and sign-out both mean "browser".
    setViewerTimeZone(next?.profile?.timezone || null);
    setUser(next);
  };
  // The org-wide envelope /auth/me carries beside the user (translation,
  // feature flags), cached on the context like the boot check does.
  const applyMeEnvelope = (response: { data?: unknown } | null) => {
    const data = response?.data as { translation?: TranslationSettings; features?: FeatureFlags } | undefined;
    if (data?.translation) setTranslation(data.translation);
    if (data?.features) setFeatures(data.features);
  };
  // The `auth:cleared` listener subscribes once; it reads adoptUser through a
  // ref (same pattern as refreshRef below).
  const adoptUserRef = useRef(adoptUser);
  adoptUserRef.current = adoptUser;
  // #2429: who was signed in when a mid-session 401 arrives.
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  // #2429: where a completed sign-in lands — the page the person was on when
  // the signed-out redirect stashed it, else the role's home. Called only at
  // the final adoption here. A sign-in that detours through MFA completes in
  // MfaVerifyPage, which applies the same rule itself.
  const landingFor = (u: User) => consumeReturnPath(u) ?? homeFor(u.role);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Restore impersonation session if it was active
      const savedImpersonation = sessionStorage.getItem(IMPERSONATION_KEY);
      if (savedImpersonation) {
        try {
          const parsed = JSON.parse(savedImpersonation) as ImpersonationState;
          // The saved targetUser is the raw doc the impersonate endpoint
          // returned — it has no `permissions` (computed only by /me), and
          // this early return skips the boot /me below. Re-read it so an
          // impersonated Lead TRFer survives a reload on a permission-gated
          // route (#1127, same shape as B-071). Best-effort: a /me failure
          // degrades to the permissions-less saved target rather than
          // dropping the session.
          let me: Awaited<ReturnType<typeof api.auth.getCurrentUser>> | null = null;
          let signedOut = false;
          try {
            me = await api.auth.getCurrentUser();
          } catch (error) {
            // permissions arrive on next /me — unless the 401 says nobody is signed in
            signedOut = axios.isAxiosError(error) && error.response?.status === 401;
          }
          const meUser = me?.status === 'success' && me.data ? me.data.user : null;
          if (!meUser && signedOut) {
            // #2478 follow-up: /me and the cookie refresh both 401'd, so the
            // operator's session is gone (signed out in another tab, which
            // reloads this one). Adopting the saved target would show the
            // family as the signed-in account with no token behind it. The
            // catch below drops the impersonation and the boot continues as
            // an ordinary signed-out load.
            throw new Error('impersonation session ended');
          }
          if (meUser && accountIdOf(meUser) !== accountIdOf(parsed.targetUser)) {
            // #2936: /me answered for someone else. On web the target's access
            // token lived in memory only, so the reload lost it; /me 401'd,
            // the interceptor refreshed from the OPERATOR's httpOnly cookie,
            // and /me answered as the operator. Keeping the session would put
            // the staff view under a banner naming the family. End it cleanly:
            // the operator /me returned, on their home, told why. Keeping the
            // view across a reload would mean storing the family's token in
            // web storage, a token-storage policy call (#2478), not made here.
            // The session goes first, so adoptUser reads the operator as both
            // the user and the tab's owner: the owner the other tabs know
            // (the operator, all through the impersonation) does not change.
            sessionStorage.removeItem(IMPERSONATION_KEY);
            setImpersonation(null);
            adoptUser(meUser);
            applyMeEnvelope(me);
            setLoading(false);
            // /me's role, not originalUser.role: it is the server's ACTIVE
            // role now, and the route guards follow it (B-596).
            navigate(homeFor(meUser.role), { replace: true });
            toast({
              title: 'Impersonation ended when the page reloaded',
              description: impersonationRestartHint(meUser.role),
            });
            return;
          }
          setImpersonation(parsed);
          adoptUser(meUser ?? parsed.targetUser);
          if (meUser) applyMeEnvelope(me);
          setLoading(false);
          return;
        } catch {
          sessionStorage.removeItem(IMPERSONATION_KEY);
        }
      }

      const token = await authStorage.get('auth_token');
      // Native: no stored token means logged out — short-circuit before /me.
      // Web: the access token lives in memory and is gone after a reload, but a
      // valid httpOnly refresh cookie may still exist. Don't early-return —
      // call /me anyway; with no access token it 401s, the response interceptor
      // refreshes from the cookie and retries, and /me succeeds if the cookie
      // is valid. If there's no valid cookie, the interceptor clears + the
      // catch below leaves the user logged out.
      const native = isNative();
      if (native && !token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.auth.getCurrentUser();
        if (response.status === 'success' && response.data) {
          adoptUser(response.data.user);
          // /auth/me carries the org-wide translation envelope (enabled +
          // disclosureMode). Cache it on the auth context so handoff
          // chat surfaces don't need a separate fetch.
          const t = (response.data as { translation?: TranslationSettings }).translation;
          if (t) setTranslation(t);
          const f = (response.data as { features?: FeatureFlags }).features;
          if (f) setFeatures(f);
        } else {
          clearAuthTokens();
          // #2429 round 3: see the 401 branch below.
          markSignedInTabEnded();
        }
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        // A logged-out cold load is expected to fail the boot auth check: /auth/me
        // 401s with no in-memory access token, and the cookie-refresh retry then
        // 401s when there's no refresh cookie at all (web, #2605; 400 before) — or the original 401
        // propagates (native). These are normal "not signed in" outcomes, not
        // errors, so don't log them. Genuine failures (network, 5xx) still log.
        if (status !== 401 && status !== 400) {
          console.error('Auth check failed:', error);
          setSessionCheckFailed(true);
        }
        // Don't clear tokens on network errors or 5xx — only on explicit 401
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          clearAuthTokens();
        }
        // #2429 round 3: this tab was signed in before the reload (the
        // LocaleProvider keeps the record) and no deliberate sign-out cleared
        // it, so the session ended elsewhere — another browser's sign-out, an
        // expiry. Write the session-ended marker in her language before
        // loading ends, so the signed-out redirect says so on /login. A
        // network failure or 5xx says nothing about the session: no marker,
        // and the record stays for the next check.
        if (status === 401 || status === 400) markSignedInTabEnded();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // React to mid-session 401s. The axios interceptor calls clearAuthTokens
  // and dispatches `auth:cleared` whenever a request comes back 401 and
  // refresh fails (or there's no refresh token to begin with). We mirror
  // that into React state so RoleGatedRoute sees user=null and Navigates
  // to the appropriate home page on the next render. Routes that don't
  // require auth (e.g. /login, /privacy) just stay where they are.
  useEffect(() => {
    const handler = () => {
      // #2429: a live session ended under this person. The signed-out
      // redirect reads this to say so on /login, in her language, and to key
      // the return path to her account. A 401 with nobody signed in (an
      // anonymous cold boot) is not an expiry.
      const prev = userRef.current;
      if (prev) {
        markSessionExpired({ account: accountKeyOf(prev), lang: expiryLanguageFor(prev) });
        // The marker carries it from here (#2429 round 3).
        forgetSignedInTab();
      }
      // #2330: a mid-session 401 ends the session — drop its cache with it,
      // so whoever signs in next in this tab starts from nothing.
      // Not announced (review finding 5): a refresh that failed here, on a
      // network blip or a 5xx, says nothing about the shared cookie, and the
      // other tabs must not be reloaded out of their work for it.
      adoptUserRef.current(null, { announce: false });
      // #2641: a 401 that survived the refresh is an answer, not an outage.
      setSessionCheckFailed(false);
      setTranslation(null);
      // #2342: the flags were the signed-out session's; the next sign-in's /me
      // brings its own.
      setFeatures(null);
      setImpersonation(null);
      sessionStorage.removeItem(IMPERSONATION_KEY);
      setLoading(false);
    };
    window.addEventListener('auth:cleared', handler);
    return () => window.removeEventListener('auth:cleared', handler);
  }, []);

  // #2641: a boot check that could not answer (a 5xx while the backend
  // restarts, a network drop) is not a sign-out. The protected routes show a
  // "can't reach TRF" state instead of /login, and /me is asked again with
  // backoff until it answers. A success adopts the person; a 401/400 (the
  // refresh cookie is really gone) is the ordinary signed-out outcome.
  const [sessionRetryTick, setSessionRetryTick] = useState(0);
  const sessionRetryAttemptRef = useRef(0);
  const sessionRetryInFlightRef = useRef(false);
  const recheckSession = async () => {
    if (sessionRetryInFlightRef.current) return;
    sessionRetryInFlightRef.current = true;
    try {
      const response = await api.auth.getCurrentUser();
      if (response.status === 'success' && response.data) {
        adoptUserRef.current(response.data.user);
        const t = (response.data as { translation?: TranslationSettings }).translation;
        if (t) setTranslation(t);
        const f = (response.data as { features?: FeatureFlags }).features;
        if (f) setFeatures(f);
      } else {
        clearAuthTokens();
        markSignedInTabEnded();
      }
      sessionRetryAttemptRef.current = 0;
      setSessionCheckFailed(false);
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401 || status === 400) {
        if (status === 401) clearAuthTokens();
        markSignedInTabEnded();
        sessionRetryAttemptRef.current = 0;
        setSessionCheckFailed(false);
      } else {
        // Still unreachable: schedule the next try.
        sessionRetryAttemptRef.current += 1;
        setSessionRetryTick((n) => n + 1);
      }
    } finally {
      sessionRetryInFlightRef.current = false;
    }
  };
  const recheckSessionRef = useRef(recheckSession);
  recheckSessionRef.current = recheckSession;
  useEffect(() => {
    if (loading || !sessionCheckFailed || user) return;
    const attempt = sessionRetryAttemptRef.current;
    const delay = SESSION_RETRY_DELAYS_MS[Math.min(attempt, SESSION_RETRY_DELAYS_MS.length - 1)];
    const id = window.setTimeout(() => { void recheckSessionRef.current(); }, delay);
    return () => window.clearTimeout(id);
  }, [loading, sessionCheckFailed, user, sessionRetryTick]);
  const retrySessionCheck = () => { void recheckSessionRef.current(); };

  // #2439: the cross-tab identity guard. Another tab of this browser that
  // signs in, signs out or adopts a different user announces it; if that is
  // not who this tab holds, the tab hard-reloads (lib/auth-sync.ts). Ignored
  // during the boot check, which adopts whoever the shared cookie names.
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  useEffect(() => startAuthSync({ isBooting: () => loadingRef.current }), []);

  // B-596: the account’s active role changed in ANOTHER window (one field per
  // account, server-side), and a request from this shell just came back
  // `403 WRONG_ACTIVE_ROLE`. Re-hydrate from /me so `user.role` is the truth
  // again — RoleGatedRoute / DashboardRoute then move the page to the shell
  // that role owns — and say why, so the jump does not read as a glitch.
  // Refs, not deps: `refresh` and `toast` are re-created per render and the
  // listener must not resubscribe each time.
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const userRoleRef = useRef<User['role'] | undefined>(undefined);
  userRoleRef.current = user?.role;
  useEffect(() => {
    const handler = async (e: Event) => {
      const activeRole = (e as CustomEvent<{ activeRole?: string }>).detail?.activeRole;
      if (!activeRole || activeRole === userRoleRef.current) return;
      try {
        await refreshRef.current();
      } catch {
        return; // a failed /me is the 401 path’s business, not this one’s
      }
      const titleKey = (ROLE_RESYNC_TITLE_KEY as Record<string, (typeof ROLE_RESYNC_TITLE_KEY)[keyof typeof ROLE_RESYNC_TITLE_KEY] | undefined>)[activeRole];
      toastRef.current({
        title: titleKey ? i18n.t(titleKey) : i18n.t('feedback:roleResync.title.unknown', { role: activeRole }),
        description: i18n.t('feedback:roleResync.description'),
      });
      navigate(homeFor(activeRole as User['role']), { replace: true });
    };
    window.addEventListener(ACTIVE_ROLE_MISMATCH_EVENT, handler);
    return () => window.removeEventListener(ACTIVE_ROLE_MISMATCH_EVENT, handler);
  // navigate is stable (react-router); everything else reads through refs.
  }, [navigate]);

  // #2478: a request sent with the impersonation token came back 401. The
  // token expired (15 minutes), or the staff session it is bound to ended.
  // The interceptor does not renew it from the operator's cookie (that would
  // put the operator under the family's banner); it raises this event. End
  // the impersonation as the #2936 reload branch does: the operator's token
  // and user back, on the Users screen, told why. If the operator's own
  // session ended too, their next request 401s and the ordinary refresh path
  // signs them out. The session record is removed first and synchronously, so
  // a burst of failed requests ends it once.
  useEffect(() => {
    const handler = async () => {
      const session = liveImpersonation();
      if (!session?.originalUser) return;
      sessionStorage.removeItem(IMPERSONATION_KEY);
      await authStorage.set('auth_token', session.originalToken, { remember: authStorage.isRemembered() });
      setImpersonation(null);
      // The session record is gone, so adoptUser reads the operator as both
      // the user and the tab's owner, which has not changed.
      adoptUserRef.current(session.originalUser);
      const role = session.originalUser.role;
      navigate(usersScreenFor(role), { replace: true });
      toastRef.current({
        title: 'Impersonation ended',
        description: impersonationRestartHint(role),
      });
    };
    window.addEventListener(IMPERSONATION_ENDED_EVENT, handler);
    return () => window.removeEventListener(IMPERSONATION_ENDED_EVENT, handler);
  // navigate is stable (react-router); everything else reads through refs.
  }, [navigate]);

  const startImpersonation = async (token: string, targetUser: User, allowedMenus: string[]) => {
    const remember = authStorage.isRemembered();
    const originalToken = (await authStorage.get('auth_token')) ?? '';

    const session: ImpersonationState = {
      active: true,
      originalToken,
      originalUser: user!,
      targetUser,
      allowedMenus,
    };

    // Store the impersonation session in sessionStorage (clears on tab/browser close)
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));

    // Swap the auth token so API requests go out as the target user
    await authStorage.set('auth_token', token, { remember });
    // Identity swap → the operator's cache must not render as the target's
    // (#2330); adoptUser drops it before the target renders.
    adoptUser(targetUser);
    setImpersonation(session);
    // Upgrade the raw targetUser with the resolved `permissions` object from
    // /me — the impersonate response omits it, and navigate() below is
    // client-side so the boot loader's /me never re-runs. Without this
    // PermissionGuard bounces an impersonated Lead TRFer off every gated
    // staff route (#1127; the five B-071 sites carry the same call).
    // Best-effort: a transient /me failure degrades to the permissions-less
    // user rather than blocking the impersonation.
    try { await refresh(); } catch { /* permissions arrive on next /me */ }

    // replace: true — this is a client-side hop off the impersonation action,
    // not a page the header-back arrow should ever return to (#1191).
    navigate(homeFor(targetUser.role), { replace: true });
  };

  const stopImpersonation = async () => {
    if (!impersonation) return;
    const remember = authStorage.isRemembered();

    // Restore the original admin token
    await authStorage.set('auth_token', impersonation.originalToken, { remember });
    const originalRole = impersonation.originalUser.role;
    // Identity swap back → drop the impersonated family's cache (#2330).
    adoptUser(impersonation.originalUser);
    setImpersonation(null);
    sessionStorage.removeItem(IMPERSONATION_KEY);

    // replace: true — same reasoning as startImpersonation above (#1191).
    navigate(usersScreenFor(originalRole), { replace: true });
  };

  const login = async (credentials: LoginRequest, rememberMe = false) => {
    try {
      // Thread rememberMe into the request body so the backend sets the web
      // refresh-cookie lifetime (persistent vs session). Native ignores it.
      // Native also presents its stored trusted-device token (#763) so a
      // device that recently passed MFA skips the challenge; on web this
      // reads null (the httpOnly cookie carries it instead).
      const trustedDeviceToken = await authStorage.get('trusted_device_token');
      const response = await api.auth.login({
        ...credentials,
        rememberMe,
        ...(trustedDeviceToken ? { trusted_device_token: trustedDeviceToken } : {}),
      });

      if (response.status === 'success' && response.data) {
        // MFA required — navigate to verification page, carry rememberMe choice and channel
        if (response.data.pending_mfa && response.data.mfa_token) {
          navigate('/mfa', { state: { mfaToken: response.data.mfa_token, rememberMe, method: response.data.method } });
          return;
        }

        // #2324 — proved who they are, but must choose a new password first.
        if (response.data.password_change_required && response.data.change_token) {
          navigate('/set-new-password', { state: { changeToken: response.data.change_token, rememberMe } });
          return;
        }

        // Normal login — store tokens and set user
        if (response.data.tokens && response.data.user) {
          await authStorage.set('auth_token', response.data.tokens.accessToken, { remember: rememberMe });
          if (response.data.tokens.refreshToken) {
            await authStorage.set('refresh_token', response.data.tokens.refreshToken, { remember: rememberMe });
          }
          // #2330: a sign-in starts a new session — clear the cache after the
          // token write and before the new user renders (see adoptUser).
          adoptUser(response.data.user, { freshSession: true });
          // Upgrade the just-set user with the resolved `permissions` object
          // from /me. The /login response omits it, and navigate() below is
          // client-side so the boot loader's /me never re-runs — without this a
          // freshly-logged-in admin/Lead-TRFer is bounced off permission-gated
          // routes until a hard reload (B-071). Best-effort: a transient /me
          // failure degrades to the permissions-less user rather than blocking.
          try { await refresh(); } catch { /* permissions arrive on next /me */ }
          // Role-aware landing — applicants go to the wizard, not the parent
          // dashboard (B-109). homeFor is the same map the route guards use.
          // replace: true so the header-back arrow doesn't return to /login
          // after a successful sign-in (#1191).
          navigate(landingFor(response.data.user), { replace: true });
        } else {
          throw new Error('Login failed');
        }
      } else {
        throw new Error('Login failed');
      }
    } catch (error: unknown) {
      console.error('Login error:', error);
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; attemptsRemaining?: number };
        const appError = new AppError(data?.message || 'Login failed', 'INVALID_CREDENTIALS');
        appError.attemptsRemaining = data?.attemptsRemaining;
        throw appError;
      }
      throw new Error('Login failed', { cause: error });
    }
  };

  // MFA verify signs in through here (MfaVerifyPage writes the tokens first).
  // A sign-in → fresh session → the cache is cleared (#2330). The /mfa/verify
  // response omits the resolved `permissions`, and MfaVerifyPage navigates
  // client-side, so hydrate from /me exactly like `login` does — otherwise an
  // admin or Lead TRFer who signs in through MFA is bounced off
  // permission-gated routes until a hard reload (B-071 pattern, #2342).
  const setUserFromTokens = async (user: User) => {
    adoptUser(user, { freshSession: true });
    try { await refresh(); } catch { /* permissions arrive on next /me */ }
  };

  // Re-fetch the current user from /auth/me. Mirrors the boot loader's
  // mapping (user payload + org-wide translation envelope) so callers can
  // pick up freshly-changed server state (e.g. a cleared re-consent block).
  const refresh = async () => {
    const response = await api.auth.getCurrentUser();
    if (response.status === 'success' && response.data) {
      // Same identity → cache kept; a different active role (B-596 resync)
      // or account → cache dropped before it renders (#2330).
      adoptUser(response.data.user);
      const t = (response.data as { translation?: TranslationSettings }).translation;
      if (t) setTranslation(t);
      const f = (response.data as { features?: FeatureFlags }).features;
      if (f) setFeatures(f);
    }
  };
  refreshRef.current = refresh;

  // One landing for a sign-in response (#2324): the MFA hold, the
  // password-change hold, or a session. New sign-in paths use it; the three
  // older methods above keep their own copies (folding them is out of scope).
  const acceptSignIn = async (response: LoginResponse, rememberMe: boolean) => {
    const data = response.status === 'success' ? response.data : undefined;
    if (!data) throw new Error('Sign-in failed');
    if (data.pending_mfa && data.mfa_token) {
      navigate('/mfa', { state: { mfaToken: data.mfa_token, rememberMe, method: data.method } });
      return;
    }
    if (data.password_change_required && data.change_token) {
      navigate('/set-new-password', { state: { changeToken: data.change_token, rememberMe } });
      return;
    }
    if (!data.tokens || !data.user) throw new Error('Sign-in failed');
    await authStorage.set('auth_token', data.tokens.accessToken, { remember: rememberMe });
    if (data.tokens.refreshToken) {
      await authStorage.set('refresh_token', data.tokens.refreshToken, { remember: rememberMe });
    }
    // #2330: a sign-in starts a new session (see adoptUser).
    adoptUser(data.user, { freshSession: true });
    try { await refresh(); } catch { /* permissions arrive on next /me (B-071) */ }
    navigate(landingFor(data.user), { replace: true });
  };

  const completeRequiredPasswordChange = async (changeToken: string, newPassword: string, rememberMe = false) => {
    try {
      await acceptSignIn(await submitRequiredPasswordChange(changeToken, newPassword, rememberMe), rememberMe);
    } catch (error: unknown) {
      throw toAppError(error, 'Could not set the new password');
    }
  };

  const loginWithGoogle = async (credential: string, rememberMe = false) => {
    try {
      await acceptSignIn(await signInWithGoogle(credential, rememberMe), rememberMe);
    } catch (error: unknown) {
      throw toAppError(error, 'Google sign-in failed');
    }
  };

  const loginWithSmsCode = async (email: string, code: string, rememberMe = false) => {
    try {
      const response = await api.auth.verifySmsCode(
        email, code, rememberMe, await authStorage.get('trusted_device_token'),
      );
      if (response.status !== 'success' || !response.data) {
        throw new Error('Sign-in failed');
      }
      if (response.data.pending_mfa && response.data.mfa_token) {
        navigate('/mfa', { state: { mfaToken: response.data.mfa_token, rememberMe, method: response.data.method } });
        return;
      }

      // #2324 — proved who they are, but must choose a new password first.
      if (response.data.password_change_required && response.data.change_token) {
        navigate('/set-new-password', { state: { changeToken: response.data.change_token, rememberMe } });
        return;
      }
      if (response.data.tokens && response.data.user) {
        await authStorage.set('auth_token', response.data.tokens.accessToken, { remember: rememberMe });
        if (response.data.tokens.refreshToken) {
          await authStorage.set('refresh_token', response.data.tokens.refreshToken, { remember: rememberMe });
        }
        // #2330: a sign-in starts a new session (see adoptUser).
        adoptUser(response.data.user, { freshSession: true });
        try { await refresh(); } catch { /* permissions arrive on next /me (B-071) */ }
        // replace: true — see the `login` handler above (#1191).
        navigate(landingFor(response.data.user), { replace: true });
      } else {
        throw new Error('Sign-in failed');
      }
    } catch (error: unknown) {
      console.error('SMS sign-in error:', error);
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as { message?: string })?.message;
        throw new AppError(message || 'Invalid code', 'INVALID_CREDENTIALS');
      }
      throw new Error('Sign-in failed', { cause: error });
    }
  };

  const loginWithMagicLink = async (token: string, rememberMe = false) => {
    try {
      const response = await api.auth.verifyMagicLink(
        token, rememberMe, await authStorage.get('trusted_device_token'),
      );
      if (response.status !== 'success' || !response.data) {
        throw new Error('Sign-in failed');
      }
      if (response.data.pending_mfa && response.data.mfa_token) {
        navigate('/mfa', { state: { mfaToken: response.data.mfa_token, rememberMe, method: response.data.method } });
        return;
      }

      // #2324 — proved who they are, but must choose a new password first.
      if (response.data.password_change_required && response.data.change_token) {
        navigate('/set-new-password', { state: { changeToken: response.data.change_token, rememberMe } });
        return;
      }
      if (response.data.tokens && response.data.user) {
        await authStorage.set('auth_token', response.data.tokens.accessToken, { remember: rememberMe });
        if (response.data.tokens.refreshToken) {
          await authStorage.set('refresh_token', response.data.tokens.refreshToken, { remember: rememberMe });
        }
        // #2330: a sign-in starts a new session (see adoptUser).
        adoptUser(response.data.user, { freshSession: true });
        try { await refresh(); } catch { /* permissions arrive on next /me (B-071) */ }
        // replace: true — see the `login` handler above (#1191).
        navigate(landingFor(response.data.user), { replace: true });
      } else {
        throw new Error('Sign-in failed');
      }
    } catch (error: unknown) {
      console.error('Magic-link sign-in error:', error);
      if (axios.isAxiosError(error)) {
        // #2342: carry the server's stable `code` (MAGIC_LINK_INVALID,
        // ACCOUNT_NOT_ACTIVE) — MagicLinkPage picks its sentence by code, in
        // the reader's language, and never shows this English message.
        const data = error.response?.data as { message?: string; code?: string } | undefined;
        throw new AppError(
          data?.message || 'Invalid or expired sign-in link',
          typeof data?.code === 'string' ? data.code : undefined,
        );
      }
      throw new Error('Sign-in failed', { cause: error });
    }
  };

  const register = async (data: RegisterRequest): Promise<{ pendingApproval: boolean }> => {
    try {
      const response = await api.auth.register(data);

      if (response.status === 'success' && response.data) {
        // New flow: accounts are created in PENDING state and require admin approval.
        // Backend returns pending_approval=true with no tokens; caller is responsible
        // for showing the awaiting-approval UI rather than navigating into the app.
        if (response.data.pending_approval) {
          return { pendingApproval: true };
        }

        localStorage.setItem('mfa_onboarding_pending', 'true');
        await login({ email: data.email, password: data.password });
        return { pendingApproval: false };
      }

      throw new Error(response.message || 'Registration failed');
    } catch (error: unknown) {
      localStorage.removeItem('mfa_onboarding_pending');
      console.error('Registration error:', error);

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = (error.response?.data as { message?: string })?.message;

        if (status === 409) {
          throw new AppError(message || 'Email already registered', 'EMAIL_EXISTS');
        }

        throw new Error(message || 'Registration failed', { cause: error });
      }

      throw error instanceof Error ? error : new Error('Registration failed');
    }
  };

  const applySignup = async (
    data: { email: string; password: string; first_name: string },
    rememberMe = true,
  ) => {
    // L4-b / D5: thread rememberMe exactly as `login` does — the server's
    // apply-signup reads `req.body.rememberMe === true` for the cookie maxAge
    // and the refresh-token lifetime, and the client keeps the tokens in
    // localStorage (not sessionStorage) so closing the browser does not sign
    // the family out.
    const response = await api.auth.applySignup({ ...data, rememberMe });
    if (response.status === 'success' && response.data?.tokens && response.data?.user) {
      await authStorage.set('auth_token', response.data.tokens.accessToken, { remember: rememberMe });
      if (response.data.tokens.refreshToken) {
        await authStorage.set('refresh_token', response.data.tokens.refreshToken, { remember: rememberMe });
      }
      // #2330: a sign-up signs in — new session (see adoptUser).
      adoptUser(response.data.user, { freshSession: true });
      // B-071: hydrate resolved `permissions` + role from /me so the
      // applicant route guard sees role:'applicant' immediately after signup.
      try { await refresh(); } catch { /* permissions arrive on next /me */ }
    } else {
      throw new Error('Signup failed');
    }
  };

  const switchRole = async (role: User['role']) => {
    try {
      const response = await api.auth.switchRole(role);
      if (response.status === 'success' && response.data) {
        if (response.data.tokens) {
          const remember = authStorage.isRemembered();
          await authStorage.set('auth_token', response.data.tokens.accessToken, { remember });
          if (response.data.tokens.refreshToken) {
            await authStorage.set('refresh_token', response.data.tokens.refreshToken, { remember });
          }
        }
        // #2096: drop every cached query before the new seat renders. Same
        // hazard as the logout clear below (B-170) and for the same reason —
        // `navigate` is CLIENT-side, so no page load separates the two seats.
        // A role-scoped endpoint answers for the ACTIVE role, while its cache
        // key carries no role, so the new seat is served the old seat's
        // answer. Observed on production 2026-09-21: the parent bell read
        // 1002, the ADMIN audience's unread count, until a manual reload.
        // Placed AFTER the token write so any refetch this triggers goes out
        // with the new role's token, and BEFORE setUser so nothing renders
        // against the previous seat's data.
        queryClient.clear();
        if (response.data.user) {
          adoptUser(response.data.user);
          // /switch-role omits the resolved `permissions`; hydrate from /me so
          // the new role's permission-gated routes are reachable immediately
          // without a hard reload (B-071).
          try { await refresh(); } catch { /* permissions arrive on next /me */ }
        }
        navigate(homeFor(role), { replace: true });
      }
    } catch (error) {
      console.error('Role switch error:', error);
      throw error;
    }
  };

  const canSwitchRole = (user?.roles?.length ?? 0) > 1;

  // The body of a sign-out, with no navigate: logout() and signOutQuietly()
  // both end here. Never throws — a failed server call still clears locally.
  const endSession = async () => {
    // Read native device_token so the backend can revoke it (B-064 / M3.5).
    // Native-only; web has no push token.
    let deviceToken: string | undefined;
    if (Capacitor.isNativePlatform()) {
      try {
        const stored = await Preferences.get({ key: 'device_token' });
        deviceToken = stored.value ?? undefined;
      } catch (e) {
        console.warn('[logout] could not read device_token from Preferences', e);
      }
    }

    try {
      await api.auth.logout({ device_token: deviceToken });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear device_token so a fresh sign-in starts with a fresh registration.
      if (Capacitor.isNativePlatform()) {
        try { await Preferences.remove({ key: 'device_token' }); } catch { /* ignore */ }
        // #2340: a push tap stored for this person must not replay in the
        // next person's session (usePushDeepLink reads it after sign-in).
        try { await Preferences.remove({ key: 'pending_push_action' }); } catch { /* ignore */ }
      }
      adoptUser(null);
      setTranslation(null);
      setFeatures(null);
      // Awaited (#2439): the magic-link Continue signs the next person in
      // right after, and a late clear must not erase that person's token.
      try { await clearAuthTokens(); } catch { /* storage unavailable: nothing to clear */ }
      sessionStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem('mfa_onboarding_pending');
      // #2340: adoptUser(null) above already did this for a signed-in session;
      // repeated here so a logout can never leave per-person state behind.
      clearUserScopedStorage();
      // B-170: drop every cached query before leaving the session. `navigate`
      // is CLIENT-side, so without this the next user in this tab renders the
      // previous session's data — either another family's patient + assigned
      // TRFer (disclosure), or, when the previous session had no family, a
      // stale empty result that hides the real one. Nothing survives a logout.
      queryClient.clear();
      // #2429: a deliberate sign-out never resumes — the next person on this
      // device must not be handed the previous person's page.
      clearReturnPath();
    }
  };

  const logout = async () => {
    // If impersonating, stop cleanly first
    if (impersonation) {
      await stopImpersonation();
      return;
    }
    await endSession();
    navigate('/login');
  };

  // #2439: a magic link for B opened where A is still signed in, and A chose
  // Continue. The ordinary sign-out without the navigate: one default logout
  // with A's stored token, then the local clears. The caller verifies next.
  // An impersonation is a session too: the operator's own token is restored
  // first, so it is the operator's session that ends.
  const signOutQuietly = async () => {
    if (impersonation) {
      await authStorage.set('auth_token', impersonation.originalToken, { remember: authStorage.isRemembered() });
      setImpersonation(null);
    }
    await endSession();
  };

  const value: AuthContextType = {
    user,
    translation,
    features,
    loading,
    sessionCheckFailed,
    retrySessionCheck,
    login,
    loginWithMagicLink,
    loginWithSmsCode,
    completeRequiredPasswordChange,
    loginWithGoogle,
    register,
    applySignup,
    logout,
    signOutQuietly,
    switchRole,
    isAuthenticated: !!user,
    canSwitchRole,
    setUserFromTokens,
    refresh,
    impersonation,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * `useAuth` for a control that only DECIDES whether to show itself: null
 * outside an AuthProvider instead of a throw. For a page whose tests render
 * it bare, where the control simply stays hidden (#2133, the family CSV
 * export on the record page). Anything that acts on the session uses
 * `useAuth`.
 */
export function useOptionalAuth() {
  return useContext(AuthContext) ?? null;
}

/**
 * SIGNED-OUT PREVIEW (#1039 PR-E, D-CS-12).
 *
 * A staff surface that previews a PRE-AUTH family screen has a problem the
 * preview itself creates: the staff user is signed in, so `AuthShell`'s topbar
 * renders “My account” with their own email in its tooltip — on a screen every
 * family reaches with no account at all, where it reads “Sign in”. The preview
 * would be showing a page nobody will ever see, on the surface whose entire job
 * is showing what a family WILL see. Same class as the 393px box that rendered
 * the desktop layout squeezed.
 *
 * So the preview overrides the session for its own subtree only. Not a prop on
 * `AuthShell` or `StevensTopbar`: those are family-facing components and should
 * not grow a parameter that exists for a staff screen — the same reasoning, and
 * the same shape, as `CopyPreviewProvider` over `useCopy`.
 *
 * Every mutator throws rather than no-opping. Nothing inside a preview may sign
 * anyone in or out, and a silent no-op would hide the day something tried.
 */
const previewRefusal = (what: string) => () => {
  throw new Error(`${what} cannot run inside a preview`);
};

export function SignedOutPreviewProvider({ children }: { children: React.ReactNode }) {
  const value: AuthContextType = {
    user: null,
    translation: null,
    features: null,
    // Resolved, not hydrating: a family reaching this screen has settled into
    // signed-out, and `loading: true` would render the topbar's placeholder.
    loading: false,
    sessionCheckFailed: false,
    retrySessionCheck: previewRefusal('Checking the session'),
    isAuthenticated: false,
    canSwitchRole: false,
    impersonation: null,
    login: previewRefusal('Signing in'),
    loginWithMagicLink: previewRefusal('Signing in'),
    loginWithSmsCode: previewRefusal('Signing in'),
    completeRequiredPasswordChange: previewRefusal('Signing in'),
    loginWithGoogle: previewRefusal('Signing in'),
    register: previewRefusal('Registering'),
    applySignup: previewRefusal('Signing up'),
    logout: previewRefusal('Signing out'),
    signOutQuietly: previewRefusal('Signing out'),
    switchRole: previewRefusal('Switching role'),
    setUserFromTokens: previewRefusal('Signing in'),
    refresh: previewRefusal('Refreshing the session'),
    startImpersonation: previewRefusal('Impersonation'),
    stopImpersonation: previewRefusal('Impersonation'),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
